import { IconFilterOff } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
	Button,
	Flex,
	Grid,
	Input,
	Select,
	Space,
	Tooltip,
	Typography,
} from "antd";
import { format } from "date-fns";
import { lazy, Suspense, useMemo, useState } from "react";
import {
	AUDIT_ENTITY_TYPES,
	type AuditFilters,
} from "@/common/artemis-operations/audit";
import { useOrgUsers } from "@/common/artemis-operations/members";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import type { PagedList } from "@/common/artemis-operations/use-paged-list";
import { useWorkspaces } from "@/common/artemis-operations/workspaces";
import type { AuditAction, AuditLog } from "@/common/model";
import { toLabel } from "@/common/utils";
import { CustomIcon, DateField, IdField } from "../base";
import { CustomDrawer } from "../layout/workspace/custom-drawer";
import {
	ResponsiveTable,
	type ResponsiveTableProps,
} from "../responsive-table";
import { TableFilterBar } from "../table-filter-bar";

// Monaco's diff view is only needed once a log row is opened — load it on demand
// instead of bundling it into every page that renders this table.
const DiffEditor = lazy(() =>
	import("@monaco-editor/react").then((m) => ({ default: m.DiffEditor })),
);

const AuditActionLabelMap: Record<AuditAction, string> = {
	CREATED: "Created",
	UPDATED: "Updated",
	DELETED: "Deleted",
	RESTORED: "Restored",
	PURGED: "Purged",
};

// Entity types with a workspace-scoped detail page, mapped to their route segment.
const ENTITY_ROUTE_SEGMENT: Partial<Record<string, string>> = {
	Agent: "agents",
	Report: "reports",
	Environment: "environments",
	Job: "jobs",
	Project: "projects",
	Task: "tasks",
};

/** Detail-page URL for the audit subject, or undefined when it has no page (or no longer exists). */
function entityLink(
	orgSlug: string | undefined,
	a: AuditLog,
): string | undefined {
	if (!orgSlug) return undefined;
	if (a.action === "DELETED" || a.action === "PURGED") return undefined;
	if (a.entityType === "Workspace")
		return `/${orgSlug}/workspaces/${a.entityId}`;
	const segment = ENTITY_ROUTE_SEGMENT[a.entityType];
	if (!segment || !a.workspaceId) return undefined;
	return `/${orgSlug}/workspaces/${a.workspaceId}/${segment}/${a.entityId}`;
}

/** Pretty-prints a stored JSON snapshot, falling back to the raw string. */
function formatSnapshot(json: string | null): string {
	if (!json) return "";
	try {
		return JSON.stringify(JSON.parse(json), null, 2);
	} catch {
		return json;
	}
}

type AuditTableProps = Omit<ResponsiveTableProps<AuditLog>, "columns"> & {
	/** Current filter state (server-side). */
	filters: AuditFilters;
	/** Called when a filter control changes; the parent re-fetches with the new `where`. */
	onFiltersChange: (filters: AuditFilters) => void;
	/** Hide the workspace filter + column when the table is already scoped to one workspace. */
	hideWorkspaceFilter?: boolean;
};

const AUDIT_FILTER_KEYS = ["workspaceId", "action", "entityType"] as const;

/**
 * Wires a `useAuditLogsPaged` list into {@link AuditTable}: server-side sort, cursor paging
 * with the "Page X of Y" footer, and the filter bar mapped onto the list's filters/search.
 */
export function auditTableListProps(list: PagedList<AuditLog>) {
	return {
		data: list.rows,
		loading: list.isFetching,
		onRefresh: () => list.refetch(),
		filters: {
			...list.filters,
			search: list.search || undefined,
		} as AuditFilters,
		onFiltersChange: (next: AuditFilters) => {
			for (const key of AUDIT_FILTER_KEYS) {
				if (next[key] !== list.filters[key]) list.setFilter(key, next[key]);
			}
			if ((next.search ?? "") !== list.search)
				list.setSearch(next.search ?? "");
		},
		pageSize: list.pageSize,
		totalCount: list.totalCount,
		pageIndex: list.pageIndex,
		hasNextPage: list.hasNextPage,
		hasPreviousPage: list.hasPreviousPage,
		onNextPage: list.onNextPage,
		onPreviousPage: list.onPreviousPage,
		manualSorting: true,
		initialSorting: list.sort,
		onSortingChange: list.setSort,
	} satisfies Partial<AuditTableProps>;
}

/**
 * Organization-wide audit trail table (ADR-036). Data and filter state are owned by the parent;
 * the filter controls here drive a server-side query. Row click shows the before/after snapshot.
 */
export function AuditTable({
	filters,
	onFiltersChange,
	hideWorkspaceFilter,
	...props
}: AuditTableProps) {
	const screens = Grid.useBreakpoint();
	const isCompact = !screens.lg;

	const [current, setCurrent] = useState<AuditLog | null>(null);
	// Local text for the search box; committed to the server filter on Enter/clear.
	const [searchText, setSearchText] = useState(filters.search ?? "");

	const workspaces = useWorkspaces();
	const orgUsers = useOrgUsers();
	const orgSlug = useRouteOrgSlug();

	const workspaceOptions = useMemo(
		() =>
			(workspaces.data ?? [])
				.filter((w): w is NonNullable<typeof w> => !!w)
				.map((w) => ({ value: w.id, label: w.name })),
		[workspaces.data],
	);

	const workspaceName = useMemo(() => {
		const map = new Map<string, string>();
		for (const w of workspaces.data ?? []) {
			if (w) map.set(w.id, w.name);
		}
		return map;
	}, [workspaces.data]);

	const actorName = useMemo(() => {
		const map = new Map<string, string>();
		for (const u of orgUsers.data ?? [])
			map.set(u.id, u.displayName || u.email || u.id);
		return map;
	}, [orgUsers.data]);

	const columns: ColumnDef<AuditLog>[] = useMemo(
		() => [
			{
				accessorKey: "action",
				header: "Action",
				size: 140,
				enableSorting: true,
				// Plain text: tags are reserved for state, and an action is an event, not a state.
				cell: ({ row }) => AuditActionLabelMap[row.original.action],
			},
			{
				accessorKey: "entityType",
				header: "Entity",
				size: 140,
				cell: ({ row }) => row.original.entityType,
			},
			{
				accessorKey: "entityId",
				header: "Id",
				size: 180,
				// stopPropagation so the link navigates instead of also opening the row drawer.
				cell: ({ row }) => (
					// biome-ignore lint/a11y/noStaticElementInteractions: click-swallowing wrapper only
					// biome-ignore lint/a11y/useKeyWithClickEvents: click-swallowing wrapper only
					<span onClick={(e) => e.stopPropagation()}>
						<IdField
							id={row.original.entityId}
							link={entityLink(orgSlug, row.original)}
						/>
					</span>
				),
			},
			...(hideWorkspaceFilter
				? []
				: [
						{
							accessorKey: "workspaceId",
							header: "Workspace",
							size: 180,
							cell: ({ row }: { row: { original: AuditLog } }) =>
								row.original.workspaceId ? (
									(toLabel(workspaceName.get(row.original.workspaceId)) ?? (
										<IdField id={row.original.workspaceId} />
									))
								) : (
									<>—</>
								),
						},
					]),
			{
				id: "actor",
				header: "Actor",
				size: 180,
				cell: ({ row }) => {
					const a = row.original;
					const label = a.actorUserId
						? (actorName.get(a.actorUserId) ?? a.actorUserId)
						: (a.actorClientId ?? a.actorSubject);
					return (
						<Typography.Text ellipsis={{ tooltip: a.actorSubject }}>
							{label}
						</Typography.Text>
					);
				},
			},
			{
				accessorKey: "occurredAt",
				header: "When",
				enableSorting: true,
				cell: ({ row }) => (
					<DateField value={row.original.occurredAt as string} />
				),
			},
		],
		[orgSlug, hideWorkspaceFilter, workspaceName, actorName],
	);

	return (
		<>
			<ResponsiveTable<AuditLog>
				columns={columns}
				initialSorting={[{ id: "occurredAt", desc: true }]}
				onRowClick={(entry) => setCurrent(entry)}
				filterBar={() => (
					<TableFilterBar isCompact={isCompact}>
						<Input.Search
							placeholder="Search by entity"
							allowClear
							style={{ width: isCompact ? "100%" : 200 }}
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							onSearch={(v) =>
								onFiltersChange({ ...filters, search: v || undefined })
							}
						/>
						{!hideWorkspaceFilter && (
							<Select
								allowClear
								placeholder="Workspace"
								style={{ width: isCompact ? "100%" : 200 }}
								value={filters.workspaceId ?? null}
								onChange={(v) =>
									onFiltersChange({ ...filters, workspaceId: v ?? undefined })
								}
								options={workspaceOptions}
							/>
						)}
						<Select
							allowClear
							showSearch
							placeholder="Entity type"
							style={{ width: isCompact ? "100%" : 160 }}
							value={filters.entityType ?? null}
							onChange={(v) =>
								onFiltersChange({ ...filters, entityType: v ?? undefined })
							}
							options={AUDIT_ENTITY_TYPES.map((t) => ({
								value: t,
								label: t,
							}))}
						/>
						<Select
							allowClear
							placeholder="Action"
							style={{ width: isCompact ? "100%" : 140 }}
							value={filters.action ?? null}
							onChange={(v) =>
								onFiltersChange({
									...filters,
									action: (v as AuditAction) ?? undefined,
								})
							}
							options={Object.keys(AuditActionLabelMap).map((a) => ({
								value: a,
								label: AuditActionLabelMap[a as AuditAction],
							}))}
						/>
						{(searchText ||
							filters.action ||
							filters.entityType ||
							(!hideWorkspaceFilter && filters.workspaceId)) && (
							<Tooltip title="Reset filters">
								<Button
									type="text"
									icon={<CustomIcon icon={IconFilterOff} />}
									onClick={() => {
										setSearchText("");
										onFiltersChange({
											...filters,
											search: undefined,
											action: undefined,
											entityType: undefined,
											...(hideWorkspaceFilter
												? {}
												: { workspaceId: undefined }),
										});
									}}
								/>
							</Tooltip>
						)}
					</TableFilterBar>
				)}
				compactRender={(r) => (
					<Flex align="start" justify="space-between" style={{ width: "100%" }}>
						<Space vertical>
							{/* biome-ignore lint/a11y/noStaticElementInteractions: click-swallowing wrapper only */}
							{/* biome-ignore lint/a11y/useKeyWithClickEvents: click-swallowing wrapper only */}
							<span onClick={(e) => e.stopPropagation()}>
								<IdField id={r.entityId} link={entityLink(orgSlug, r)} />
							</span>
							<Typography.Text type="secondary">{r.entityType}</Typography.Text>
						</Space>
						<Typography.Text>{AuditActionLabelMap[r.action]}</Typography.Text>
					</Flex>
				)}
				{...props}
			/>
			<CustomDrawer
				open={!!current}
				onClose={() => setCurrent(null)}
				size="large"
				title={
					current
						? `${current.action} ${current.entityType} ${current.entityId}`
						: ""
				}
			>
				{current && (
					<Space vertical size="large" style={{ width: "100%" }}>
						<Typography.Text type="secondary">
							{format(new Date(current.occurredAt as string), "PPpp")} ·{" "}
							{current.actorSubject}
						</Typography.Text>
						<Suspense fallback={null}>
							<DiffEditor
								height="70vh"
								language="json"
								original={formatSnapshot(current.before)}
								modified={formatSnapshot(current.after)}
								theme="vs-light"
								options={{
									readOnly: true,
									minimap: { enabled: false },
									fontSize: 12,
									scrollBeyondLastLine: false,
									renderOverviewRuler: false,
									hideUnchangedRegions: { enabled: true },
								}}
							/>
						</Suspense>
					</Space>
				)}
			</CustomDrawer>
		</>
	);
}
